import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hostListProperties, hostDeleteProperty } from '../api/host';

const statusTone = (status) => {
  const value = (status || '').toLowerCase();
  if (value === 'published' || value === 'approved') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (value === 'pending') return 'bg-amber-50 text-amber-700 border-amber-100';
  if (value === 'suspended') return 'bg-rose-50 text-rose-700 border-rose-100';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

const statusLabel = (status) => {
  const value = (status || '').toLowerCase();
  if (value === 'pending') return 'Pending approval';
  if (value === 'published' || value === 'approved') return 'Published';
  if (value === 'suspended') return 'Suspended';
  return 'Draft';
};

const formatPrice = (amount, currency = '₹') => {
  if (amount === undefined || amount === null || amount === '') return '-';
  const num = Number(amount);
  if (Number.isNaN(num)) return '-';
  return `${currency}${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const HostProperties = ({ accessToken }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = async (nextPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await hostListProperties(accessToken, {
        page: nextPage,
        limit,
        q: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      const list = res?.items || [];
      setItems(list);
      setTotal(res?.total ?? list.length);
      setPage(nextPage);
    } catch (err) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [statusFilter, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm('Delete property?')) return;
    try {
      await hostDeleteProperty(accessToken, id);
      await load(page);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const publishedCount = useMemo(
    () => items.filter((p) => (p.status || '').toLowerCase() === 'published').length,
    [items]
  );
  const draftCount = useMemo(() => items.filter((p) => (p.status || '').toLowerCase() === 'draft').length, [items]);
  const pendingCount = useMemo(() => items.filter((p) => (p.status || '').toLowerCase() === 'pending').length, [items]);

  const locationLine = (p) => [p.city, p.state, p.country].filter(Boolean).join(', ') || p.address_line || '-';

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
          <h1 className="text-2xl font-bold text-slate-900">My Properties</h1>
          <p className="text-sm text-slate-600">Manage your listings, pricing, and status.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/host/properties/new')}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          Add Property
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-lg font-semibold text-slate-900">{total}</p>
        </div>
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
          <p className="text-xs text-emerald-600">Published</p>
          <p className="text-lg font-semibold text-emerald-800">{publishedCount}</p>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-600">Pending approval</p>
          <p className="text-lg font-semibold text-amber-800">{pendingCount}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-xs text-slate-500">Drafts</p>
          <p className="text-lg font-semibold text-slate-900">{draftCount}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, city, or code"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="pending">Pending approval</option>
          <option value="draft">Draft</option>
          <option value="suspended">Suspended</option>
        </select>
        <button
          type="button"
          onClick={() => load(1)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{error}</div>}
      {loading && <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600">Loading...</div>}
      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-600">
          <p className="font-semibold text-slate-800 mb-2">No properties yet.</p>
          <p className="mb-3">Add your first listing to start taking bookings.</p>
          <button
            type="button"
            onClick={() => navigate('/host/properties/new')}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Add Property
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="grid gap-4 p-4 lg:grid-cols-2">
            {items.map((p) => (
              <div key={p.id} className="flex gap-3 rounded-xl border border-slate-100 p-3 shadow-sm hover:border-emerald-100">
                <div className="h-28 w-36 overflow-hidden rounded-lg bg-slate-100">
                  {p.cover_image?.image_url ? (
                    <img
                      src={p.cover_image.image_url}
                      alt={p.cover_image.alt_text || p.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-500">No image</div>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{p.title}</p>
                      <p className="text-xs text-slate-500">{locationLine(p)}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${statusTone(p.status)}`}>
                      {statusLabel(p.status)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600">
                    Weekday {formatPrice(p.base_weekday_price || p.base_price_per_night, p.currency ? `${p.currency} ` : '₹')}
                    {' · '}Weekend {formatPrice(p.base_weekend_price || p.weekend_price_per_night, p.currency ? `${p.currency} ` : '₹')}
                  </div>
                  <div className="flex items-center gap-3 text-sm font-semibold">
                    <Link className="text-emerald-700 hover:underline" to={`/host/properties/${p.id}/edit`}>
                      Edit
                    </Link>
                    <Link className="text-blue-700 hover:underline" to={`/host/calendar?propertyId=${p.id}`}>
                      Calendar & Pricing
                    </Link>
                    <button className="text-rose-600 hover:underline" onClick={() => remove(p.id)} type="button">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
            <span>
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="space-x-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => load(page - 1)}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => load(page + 1)}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostProperties;
